import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Users, Clock, Calendar, Download, FileText, Plus, Edit2, 
  CheckCircle, AlertCircle, History, Lock, Filter, RefreshCw,
  QrCode, Printer, Bell, BellOff, BellRing, Trash2, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { useShiftAlarms } from '../../hooks/useShiftAlarms';
import CorrectionRequestsPanel from './CorrectionRequestsPanel';
import { madridTime, madridLocalInput } from '../../lib/utils';
import { DateRangePicker } from './common/DateRangePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeClockAdminPanel = () => {
  // State
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [todayStats, setTodayStats] = useState(null);
  const [weekStats, setWeekStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [customRange, setCustomRange] = useState(() => {
    const n = new Date();
    const p = (x) => String(x).padStart(2, '0');
    return { from: `${n.getFullYear()}-${p(n.getMonth() + 1)}-01`, to: `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}` };
  });
  const useRange = selectedMonth === 'rango';
  const exportMonth = useRange ? (customRange.from || new Date().toISOString()).slice(0, 7) : selectedMonth;
  // Pending correction-requests count for the bell badge
  const [pendingCorrCount, setPendingCorrCount] = useState(0);
  
  // Dialogs
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [schedulingEmployee, setSchedulingEmployee] = useState(null);
  const [scheduleForm, setScheduleForm] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [correctingRecord, setCorrectingRecord] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const qrCanvasRef = useRef(null);
  
  // Form data
  const [employeeForm, setEmployeeForm] = useState({ name: '', pin: '', role: 'staff', contract_hours_weekly: 40 });
  const [correctionForm, setCorrectionForm] = useState({ new_clock_in: '', new_clock_out: '', reason: '' });
  const [showManualRecordDialog, setShowManualRecordDialog] = useState(false);
  const [manualRecordForm, setManualRecordForm] = useState({ employee_id: '', date: '', clock_in: '', clock_out: '', reason: '' });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const { alarms, currentTime, hasAlarms, fetchAlarms: refreshAlarms } = useShiftAlarms();

  const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  // Toggle alarms for an employee
  const handleToggleAlarms = async (emp) => {
    try {
      const headers = getAuthHeaders();
      const newVal = !(emp.alarms_enabled !== false);
      const res = await fetch(`${API_URL}/api/timeclock/employees/${emp.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ alarms_enabled: newVal })
      });
      if (res.ok) {
        toast.success(newVal ? `Alarmas activadas para ${emp.name}` : `Alarmas desactivadas para ${emp.name}`);
        fetchData();
        refreshAlarms();
      }
    } catch (error) {
      toast.error('Error al actualizar alarmas');
    }
  };

  // Open schedule editor
  const handleEditSchedule = (emp) => {
    setSchedulingEmployee(emp);
    const existing = emp.schedule || [];
    // Build form with all 7 days
    const form = [];
    for (let d = 0; d < 7; d++) {
      const entry = existing.find(s => s.day_of_week === d);
      form.push({
        day_of_week: d,
        enabled: !!entry,
        clock_in: entry?.clock_in || '12:00',
        clock_out: entry?.clock_out || '16:00',
      });
    }
    setScheduleForm(form);
    setShowScheduleDialog(true);
  };

  // Save schedule
  const handleSaveSchedule = async () => {
    if (!schedulingEmployee) return;
    const schedule = scheduleForm
      .filter(s => s.enabled)
      .map(s => ({ day_of_week: s.day_of_week, clock_in: s.clock_in, clock_out: s.clock_out }));
    
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timeclock/employees/${schedulingEmployee.id}/schedule`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ schedule })
      });
      if (res.ok) {
        toast.success(`Horario actualizado para ${schedulingEmployee.name}`);
        setShowScheduleDialog(false);
        setSchedulingEmployee(null);
        fetchData();
        refreshAlarms();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al guardar horario');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      const [empRes, statsRes, weekRes] = await Promise.all([
        fetch(`${API_URL}/api/timeclock/employees`, { headers }),
        fetch(`${API_URL}/api/timeclock/stats/today`, { headers }),
        fetch(`${API_URL}/api/timeclock/stats/week`, { headers })
      ]);

      if (empRes.ok) setEmployees(await empRes.json());
      if (statsRes.ok) setTodayStats(await statsRes.json());
      if (weekRes.ok) setWeekStats(await weekRes.json());

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchRecords = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      let url = `${API_URL}/api/timeclock/records?`;
      
      if (selectedEmployee !== 'all') url += `employee_id=${selectedEmployee}&`;
      if (useRange) {
        if (customRange.from) url += `start_date=${customRange.from}&`;
        if (customRange.to) url += `end_date=${customRange.to}`;
      } else if (selectedMonth) {
        url += `start_date=${selectedMonth}-01&end_date=${selectedMonth}-31`;
      }

      const res = await fetch(url, { headers });
      if (res.ok) setRecords(await res.json());

      // Also fetch summary
      let summaryUrl = useRange
        ? `${API_URL}/api/timeclock/records/summary?${customRange.from ? `start_date=${customRange.from}&` : ''}${customRange.to ? `end_date=${customRange.to}` : ''}`
        : `${API_URL}/api/timeclock/records/summary?month=${selectedMonth}`;
      if (selectedEmployee !== 'all') summaryUrl += `&employee_id=${selectedEmployee}`;
      
      const summaryRes = await fetch(summaryUrl, { headers });
      if (summaryRes.ok) setSummary(await summaryRes.json());

    } catch (error) {
      console.error('Error fetching records:', error);
    }
  }, [getAuthHeaders, selectedEmployee, selectedMonth, useRange, customRange]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timeclock/audit-logs`, { headers });
      if (res.ok) setAuditLogs(await res.json());
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll pending correction-requests count for the bell badge
  useEffect(() => {
    let active = true;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await fetch(`${API_URL}/api/timeclock/correction-requests/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (active) setPendingCorrCount(data?.count || 0);
      } catch (_) { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // every 60s
    return () => { active = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, selectedEmployee, selectedMonth]);

  // Generate QR Code for /fichar URL
  const generateQRCode = useCallback(async () => {
    try {
      const ficharUrl = `${window.location.origin}/fichar`;
      const dataUrl = await QRCode.toDataURL(ficharUrl, {
        width: 400,
        margin: 2,
        color: {
          dark: '#3D2E1F',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR:', error);
    }
  }, []);

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  // Print QR Code
  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Fichaje - PONT 10</title>
        <style>
          body {
            font-family: 'Segoe UI', system-ui, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            background: white;
          }
          .container {
            text-align: center;
            padding: 40px;
            border: 3px solid #3D2E1F;
            border-radius: 20px;
            max-width: 400px;
          }
          h1 {
            font-size: 32px;
            color: #3D2E1F;
            margin: 0 0 10px 0;
            letter-spacing: 2px;
          }
          h2 {
            font-size: 24px;
            color: #5C4A30;
            margin: 0 0 30px 0;
            font-weight: 400;
          }
          img {
            width: 300px;
            height: 300px;
          }
          p {
            margin-top: 20px;
            font-size: 16px;
            color: #7A6B4E;
          }
          .instructions {
            margin-top: 30px;
            padding: 20px;
            background: #F5F5F0;
            border-radius: 12px;
            font-size: 14px;
            color: #5C4A30;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>PONT 10</h1>
          <h2>Control Horario</h2>
          <img src="${qrCodeDataUrl}" alt="QR Fichaje" />
          <p>Escanea para fichar entrada/salida</p>
          <div class="instructions">
            <strong>Instrucciones:</strong><br/>
            1. Abre la cámara de tu móvil<br/>
            2. Apunta al código QR<br/>
            3. Introduce tu PIN personal<br/>
            4. Pulsa INICIAR o FINALIZAR JORNADA
          </div>
        </div>
        <script>window.onload = () => { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Download QR Code
  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.download = 'qr_fichaje_pont10.png';
    link.href = qrCodeDataUrl;
    link.click();
    toast.success('QR descargado');
  };

  // Employee CRUD
  const handleSaveEmployee = async () => {
    const isEdit = !!editingEmployee;
    if ((!isEdit || employeeForm.pin) && !/^\d{4}$/.test(employeeForm.pin)) {
      toast.error('El PIN debe tener exactamente 4 cifras');
      return;
    }
    try {
      const headers = getAuthHeaders();
      
      const url = isEdit 
        ? `${API_URL}/api/timeclock/employees/${editingEmployee.id}`
        : `${API_URL}/api/timeclock/employees`;
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({ ...employeeForm, pin: employeeForm.pin || null })
      });

      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Error al guardar');
        return;
      }

      toast.success(isEdit ? 'Empleado actualizado' : 'Empleado creado');
      setShowEmployeeDialog(false);
      setEditingEmployee(null);
      setEmployeeForm({ name: '', pin: '', role: 'staff', contract_hours_weekly: 40 });
      fetchData();
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      pin: emp.pin_display || '',
      role: emp.role,
      contract_hours_weekly: emp.contract_hours_weekly
    });
    setShowEmployeeDialog(true);
  };

  const handleToggleActive = async (emp) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timeclock/employees/${emp.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !emp.is_active })
      });

      if (res.ok) {
        toast.success(emp.is_active ? 'Empleado desactivado' : 'Empleado activado');
        fetchData();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Record full edit
  const handleCorrection = async () => {
    if (!correctionForm.reason.trim()) {
      toast.error('Debes indicar el motivo');
      return;
    }
    try {
      const headers = getAuthHeaders();
      const body = { reason: correctionForm.reason };
      if (correctionForm.new_date && correctionForm.new_date !== correctingRecord.date) body.date = correctionForm.new_date;
      if (correctionForm.new_clock_in) body.clock_in = correctionForm.new_clock_in;
      if (correctionForm.new_clock_out) body.clock_out = correctionForm.new_clock_out;

      const res = await fetch(`${API_URL}/api/timeclock/records/${correctingRecord.id}`, {
        method: 'PUT', headers, body: JSON.stringify(body)
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Error'); return; }
      toast.success('Registro editado con trazabilidad');
      setShowCorrectionDialog(false);
      setCorrectingRecord(null);
      setCorrectionForm({ new_date: '', new_clock_in: '', new_clock_out: '', reason: '' });
      fetchRecords();
      fetchAuditLogs();
    } catch { toast.error('Error de conexión'); }
  };

  // Delete record
  const handleDeleteRecord = async (record) => {
    if (!window.confirm(`¿Eliminar registro de ${record.employee_name} del ${record.date}?`)) return;
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timeclock/records/${record.id}`, { method: 'DELETE', headers });
      if (!res.ok) { toast.error('Error al eliminar'); return; }
      toast.success('Registro eliminado');
      fetchRecords();
      fetchAuditLogs();
    } catch { toast.error('Error de conexión'); }
  };

  // Create manual record
  const handleCreateManualRecord = async () => {
    if (!manualRecordForm.employee_id || !manualRecordForm.date || !manualRecordForm.clock_in || !manualRecordForm.reason.trim()) {
      toast.error('Completa empleado, fecha, entrada y motivo'); return;
    }
    try {
      const headers = getAuthHeaders();
      const ci = `${manualRecordForm.date}T${manualRecordForm.clock_in}:00`;
      const co = manualRecordForm.clock_out ? `${manualRecordForm.date}T${manualRecordForm.clock_out}:00` : null;
      const res = await fetch(`${API_URL}/api/timeclock/records/manual`, {
        method: 'POST', headers,
        body: JSON.stringify({ employee_id: manualRecordForm.employee_id, date: manualRecordForm.date, clock_in: ci, clock_out: co, reason: manualRecordForm.reason })
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Error'); return; }
      toast.success('Registro manual creado');
      setShowManualRecordDialog(false);
      setManualRecordForm({ employee_id: '', date: '', clock_in: '', clock_out: '', reason: '' });
      fetchRecords();
      fetchAuditLogs();
    } catch { toast.error('Error de conexión'); }
  };

  const handleCloseRecord = async (record) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timeclock/records/${record.id}/close`, {
        method: 'POST',
        headers
      });

      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Error al cerrar registro');
        return;
      }

      toast.success('Registro cerrado');
      fetchRecords();
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // Export functions
  const handleExportCSV = async () => {
    try {
      const headers = getAuthHeaders();
      let url = useRange
        ? `${API_URL}/api/timeclock/export/csv?start_date=${customRange.from}&end_date=${customRange.to}`
        : `${API_URL}/api/timeclock/export/csv?month=${exportMonth}`;
      if (selectedEmployee !== 'all') url += `&employee_id=${selectedEmployee}`;

      const res = await fetch(url, { headers });
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || 'Error al exportar');
        return;
      }

      // Download CSV
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = data.filename;
      link.click();

      toast.success(`Exportados ${data.record_count} registros`);
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleExportPDF = async () => {
    try {
      const headers = getAuthHeaders();
      let url = `${API_URL}/api/timeclock/pdf/monthly/${exportMonth}`;
      if (selectedEmployee !== 'all') url += `?employee_id=${selectedEmployee}`;

      const res = await fetch(url, { headers });
      
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || 'Error al generar PDF');
        return;
      }

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `registro_jornada_${exportMonth}.pdf`;
      link.click();

      toast.success('PDF generado correctamente');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  // Format helpers
  const formatTime = (isoString) => {
    return madridTime(isoString);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  // Generate month options (last 12 months) — use day=1 to avoid overflow (Feb)
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="timeclock-admin-panel">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Control Horario</h1>
          <p className="text-gray-500">Gestión de fichajes y jornadas laborales</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowQRDialog(true)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <QrCode size={18} className="mr-2" /> QR Fichaje
          </Button>
          <Button onClick={() => { setEditingEmployee(null); setEmployeeForm({ name: '', pin: '', role: 'staff', contract_hours_weekly: 40 }); setShowEmployeeDialog(true); }} className="bg-amber-500 hover:bg-amber-600">
            <Plus size={18} className="mr-2" /> Añadir Empleado
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw size={18} className="mr-2" /> Actualizar
          </Button>
        </div>
      </div>

      {/* Alarm Banner */}
      {hasAlarms && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-md" data-testid="alarm-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <BellRing className="text-amber-600 animate-pulse" size={22} />
              <span className="font-semibold text-amber-800">Alarmas de turno — {currentTime}</span>
            </div>
            <div className="space-y-1">
              {alarms.map((alarm, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${alarm.type === 'clock_in' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-gray-800">{alarm.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{employees.filter(e => e.is_active).length}</p>
                <p className="text-xs text-gray-500">Empleados activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{todayStats?.currently_working || 0}</p>
                <p className="text-xs text-gray-500">Trabajando ahora</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <CheckCircle className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{todayStats?.total_hours_today || 0}h</p>
                <p className="text-xs text-gray-500">Horas hoy</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{weekStats?.total_hours || 0}h</p>
                <p className="text-xs text-gray-500">Esta semana</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Currently Working */}
      {todayStats?.working_now?.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-green-800 mb-2">Trabajando ahora:</p>
            <div className="flex flex-wrap gap-2">
              {todayStats.working_now.map((w, i) => (
                <Badge key={i} variant="secondary" className="bg-green-100 text-green-800">
                  {w.name} · desde {formatTime(w.clock_in)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="records" className="space-y-4">
        <TabsList className="flex w-full overflow-x-auto justify-start md:grid md:grid-cols-5 h-auto">
          <TabsTrigger value="records">Registros</TabsTrigger>
          <TabsTrigger value="employees">Empleados</TabsTrigger>
          <TabsTrigger value="summary">Resumen</TabsTrigger>
          <TabsTrigger value="audit" onClick={fetchAuditLogs}>Auditoría</TabsTrigger>
          <TabsTrigger value="corrections" data-testid="tab-corrections" className="relative">
            <span className="flex items-center gap-1.5">
              <BellRing className="w-3.5 h-3.5" /> Correcciones
              {pendingCorrCount > 0 && (
                <span
                  data-testid="corrections-badge-count"
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full bg-red-500 text-white"
                >
                  {pendingCorrCount}
                </span>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Records Tab */}
        <TabsContent value="records" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Empleado</label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos los empleados" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los empleados</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Mes</label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="capitalize">{opt.label}</SelectItem>
                      ))}
                      <SelectItem value="rango">Fechas…</SelectItem>
                    </SelectContent>
                  </Select>
                  {useRange && (
                    <div className="mt-2">
                      <DateRangePicker value={customRange} onChange={setCustomRange} idPrefix="timeclock-range" showPresets={false} />
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => { setShowManualRecordDialog(true); setManualRecordForm({ employee_id: '', date: new Date().toISOString().slice(0, 10), clock_in: '12:00', clock_out: '16:00', reason: '' }); }}
                    className="bg-green-600 hover:bg-green-700" data-testid="create-manual-record-btn">
                    <PlusCircle size={16} className="mr-2" /> Nuevo Registro
                  </Button>
                  <Button variant="outline" onClick={handleExportCSV} data-testid="timeclock-export-csv-btn">
                    <Download size={16} className="mr-2" /> CSV
                  </Button>
                  <Button variant="outline" onClick={handleExportPDF} data-testid="timeclock-export-pdf-btn">
                    <FileText size={16} className="mr-2" /> PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Records Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empleado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Entrada</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Salida</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Horas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Origen</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {records.map(record => {
                      const source = record.source || 'digital';
                      const sourceStyles = {
                        digital: 'bg-blue-100 text-blue-700',
                        manual: 'bg-amber-100 text-amber-700',
                        edited: 'bg-purple-100 text-purple-700',
                      };
                      return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatDate(record.date)}</td>
                        <td className="px-4 py-3 text-sm font-medium">{record.employee_name}</td>
                        <td className="px-4 py-3 text-sm text-center font-mono">{formatTime(record.clock_in)}</td>
                        <td className="px-4 py-3 text-sm text-center font-mono">{formatTime(record.clock_out)}</td>
                        <td className="px-4 py-3 text-sm text-center font-semibold">{record.total_hours || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="secondary" className={sourceStyles[source] || sourceStyles.digital}>
                            {source === 'digital' ? 'Digital' : source === 'manual' ? 'Manual' : 'Editado'}
                          </Badge>
                          {record.edit_reason && (
                            <p className="text-xs text-gray-400 mt-0.5" title={record.edit_reason}>{record.edit_reason.substring(0, 20)}{record.edit_reason.length > 20 ? '...' : ''}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setCorrectingRecord(record);
                                setCorrectionForm({
                                  new_date: record.date || '',
                                  new_clock_in: madridLocalInput(record.clock_in),
                                  new_clock_out: madridLocalInput(record.clock_out),
                                  reason: ''
                                });
                                setShowCorrectionDialog(true);
                              }}
                              title="Editar registro"
                              data-testid={`edit-record-${record.id}`}
                            >
                              <Edit2 size={14} />
                            </Button>
                            {!record.is_closed && record.clock_out && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleCloseRecord(record)}
                                title="Cerrar registro"
                              >
                                <Lock size={14} />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-400 hover:text-red-600"
                              onClick={() => handleDeleteRecord(record)}
                              title="Eliminar registro"
                              data-testid={`delete-record-${record.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                    {records.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          No hay registros para este período
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horario</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Alarmas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {employees.map(emp => {
                      const schedule = emp.schedule || [];
                      const alarmsOn = emp.alarms_enabled !== false;
                      return (
                        <tr key={emp.id} className="hover:bg-gray-50" data-testid={`employee-row-${emp.name}`}>
                          <td className="px-4 py-3 font-medium">
                            {emp.name}
                            {emp.pin_display ? (
                              <span className="ml-2 text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded" data-testid={`employee-pin-${emp.name}`}>PIN {emp.pin_display}</span>
                            ) : (
                              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`employee-pin-missing-${emp.name}`}>Asignar PIN de 4 cifras</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center capitalize">{emp.role}</td>
                          <td className="px-4 py-3">
                            {schedule.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {schedule.map((s, i) => (
                                  <span key={i} className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                    {DAY_NAMES[s.day_of_week]} {s.clock_in}-{s.clock_out}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Sin horario</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAlarms(emp)}
                              className={alarmsOn ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 hover:text-gray-400'}
                              data-testid={`alarm-toggle-${emp.name}`}
                            >
                              {alarmsOn ? <Bell size={18} /> : <BellOff size={18} />}
                            </Button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={emp.is_active ? 'default' : 'secondary'} className={emp.is_active ? 'bg-green-100 text-green-800' : ''}>
                              {emp.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditSchedule(emp)} title="Editar horario">
                                <Calendar size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEditEmployee(emp)} title="Editar empleado">
                                <Edit2 size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleToggleActive(emp)}
                                className={emp.is_active ? 'text-red-500' : 'text-green-500'}
                                title={emp.is_active ? 'Desactivar' : 'Activar'}
                              >
                                {emp.is_active ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {summary.map(emp => (
              <Card key={emp.employee_id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{emp.employee_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total horas:</span>
                      <span className="font-bold text-lg">{emp.total_hours}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Días trabajados:</span>
                      <span className="font-medium">{emp.days_worked}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Media diaria:</span>
                      <span className="font-medium">
                        {emp.days_worked > 0 ? (emp.total_hours / emp.days_worked).toFixed(1) : 0}h
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {summary.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-gray-500">
                  No hay datos para el período seleccionado
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History size={20} /> Registro de Auditoría
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha/Hora</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {auditLogs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {new Date(log.timestamp).toLocaleString('es-ES')}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant="outline" className={
                            log.action === 'CORRECTION' ? 'border-yellow-500 text-yellow-700' :
                            log.action === 'CLOCK_IN' ? 'border-green-500 text-green-700' :
                            log.action === 'CLOCK_OUT' ? 'border-red-500 text-red-700' :
                            ''
                          }>
                            {log.action}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">{log.performed_by}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {log.new_values?.reason && <span className="text-yellow-600">Motivo: {log.new_values.reason}</span>}
                          {log.ip_address && <span className="ml-2">IP: {log.ip_address}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Corrections Tab */}
        <TabsContent value="corrections">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BellRing size={20} /> Solicitudes de correcci&oacute;n
                {pendingCorrCount > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white">{pendingCorrCount} pendientes</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CorrectionRequestsPanel />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}</DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Modifica los datos del empleado' : 'Añade un nuevo empleado al sistema de fichaje'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nombre completo</label>
              <Input
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: María García"
              />
            </div>
            <div>
              <label className="text-sm font-medium">PIN (4 cifras)</label>
              <Input
                type="text"
                inputMode="numeric"
                value={employeeForm.pin}
                onChange={(e) => setEmployeeForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                placeholder={editingEmployee ? 'Escribe un PIN nuevo de 4 cifras' : '1234'}
                maxLength={4}
                data-testid="employee-pin-input"
              />
              {editingEmployee && !editingEmployee.pin_display && (
                <p className="text-xs text-amber-600 mt-1" data-testid="employee-pin-legacy-warning">
                  Este empleado tiene un PIN antiguo que no se puede mostrar. Asígnale uno nuevo de 4 cifras para que pueda fichar.
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Rol</label>
              <Select value={employeeForm.role} onValueChange={(v) => setEmployeeForm(f => ({ ...f, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Horas contrato/semana</label>
              <Input
                type="number"
                value={employeeForm.contract_hours_weekly}
                onChange={(e) => setEmployeeForm(f => ({ ...f, contract_hours_weekly: parseFloat(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveEmployee} className="bg-amber-500 hover:bg-amber-600">
              {editingEmployee ? 'Guardar cambios' : 'Crear empleado'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Edit Record Dialog */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Registro</DialogTitle>
            <DialogDescription>
              {correctingRecord?.employee_name} — {correctingRecord?.date}. Queda registrado con trazabilidad completa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <AlertCircle className="inline mr-2" size={16} />
              Esta edición quedará registrada en auditoría. Origen actual: <strong>{correctingRecord?.source || 'digital'}</strong>
            </div>
            <div>
              <label className="text-sm font-medium">Fecha</label>
              <Input
                type="date"
                value={correctionForm.new_date || ''}
                onChange={(e) => setCorrectionForm(f => ({ ...f, new_date: e.target.value }))}
                data-testid="edit-record-date"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Hora de entrada</label>
              <Input
                type="datetime-local"
                value={correctionForm.new_clock_in}
                onChange={(e) => setCorrectionForm(f => ({ ...f, new_clock_in: e.target.value }))}
                data-testid="edit-record-clock-in"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Hora de salida</label>
              <Input
                type="datetime-local"
                value={correctionForm.new_clock_out}
                onChange={(e) => setCorrectionForm(f => ({ ...f, new_clock_out: e.target.value }))}
                data-testid="edit-record-clock-out"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Motivo de la edición *</label>
              <Input
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Ej: Error fichaje, olvido de salida, turno cambiado..."
                data-testid="edit-record-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionDialog(false)}>Cancelar</Button>
            <Button onClick={handleCorrection} className="bg-yellow-500 hover:bg-yellow-600" data-testid="confirm-edit-record-btn">
              Guardar Edición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Manual Record Dialog */}
      <Dialog open={showManualRecordDialog} onOpenChange={setShowManualRecordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Registro Manual</DialogTitle>
            <DialogDescription>
              Crea un registro de fichaje manualmente. Quedará marcado como "Manual" en el sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Empleado *</label>
              <Select value={manualRecordForm.employee_id} onValueChange={v => setManualRecordForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger data-testid="manual-record-employee">
                  <SelectValue placeholder="Seleccionar empleado..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Fecha *</label>
              <Input type="date" value={manualRecordForm.date} onChange={e => setManualRecordForm(f => ({ ...f, date: e.target.value }))} data-testid="manual-record-date" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Entrada *</label>
                <Input type="time" value={manualRecordForm.clock_in} onChange={e => setManualRecordForm(f => ({ ...f, clock_in: e.target.value }))} data-testid="manual-record-clock-in" />
              </div>
              <div>
                <label className="text-sm font-medium">Salida</label>
                <Input type="time" value={manualRecordForm.clock_out} onChange={e => setManualRecordForm(f => ({ ...f, clock_out: e.target.value }))} data-testid="manual-record-clock-out" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Motivo *</label>
              <Input value={manualRecordForm.reason} onChange={e => setManualRecordForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Ej: Fichaje olvidado, turno extra, corrección día festivo..." data-testid="manual-record-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualRecordDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateManualRecord} className="bg-green-600 hover:bg-green-700" data-testid="confirm-manual-record-btn">
              <PlusCircle size={16} className="mr-2" /> Crear Registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={20} />
              QR para Fichaje
            </DialogTitle>
            <DialogDescription>
              Imprime este código QR y colócalo en la cocina para que los empleados puedan fichar fácilmente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {qrCodeDataUrl && (
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-inner">
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Fichaje" 
                  className="w-64 h-64"
                  ref={qrCanvasRef}
                />
              </div>
            )}
            <p className="text-sm text-gray-500 mt-4 text-center">
              Escanea con la cámara del móvil para abrir la página de fichaje
            </p>
            <p className="text-xs text-gray-400 mt-2 font-mono">
              {window.location.origin}/fichar
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleDownloadQR} className="flex-1">
              <Download size={16} className="mr-2" /> Descargar PNG
            </Button>
            <Button onClick={handlePrintQR} className="flex-1 bg-amber-500 hover:bg-amber-600">
              <Printer size={16} className="mr-2" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Horario de {schedulingEmployee?.name}
            </DialogTitle>
            <DialogDescription>
              Configura los días y horas de entrada/salida. Las alarmas sonarán 5 min antes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-80 overflow-y-auto">
            {scheduleForm.map((entry, idx) => (
              <div key={idx} className="flex items-center gap-3" data-testid={`schedule-day-${idx}`}>
                <label className="flex items-center gap-2 min-w-[80px]">
                  <input
                    type="checkbox"
                    checked={entry.enabled}
                    onChange={(e) => {
                      const updated = [...scheduleForm];
                      updated[idx] = { ...updated[idx], enabled: e.target.checked };
                      setScheduleForm(updated);
                    }}
                    className="w-4 h-4 rounded accent-amber-500"
                  />
                  <span className={`text-sm font-medium ${entry.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                    {DAY_NAMES[entry.day_of_week]}
                  </span>
                </label>
                {entry.enabled && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={entry.clock_in}
                      onChange={(e) => {
                        const updated = [...scheduleForm];
                        updated[idx] = { ...updated[idx], clock_in: e.target.value };
                        setScheduleForm(updated);
                      }}
                      className="w-28 text-sm"
                    />
                    <span className="text-gray-400">—</span>
                    <Input
                      type="time"
                      value={entry.clock_out}
                      onChange={(e) => {
                        const updated = [...scheduleForm];
                        updated[idx] = { ...updated[idx], clock_out: e.target.value };
                        setScheduleForm(updated);
                      }}
                      className="w-28 text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveSchedule} className="bg-amber-500 hover:bg-amber-600">
              Guardar horario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeClockAdminPanel;
